import { EXTERNAL_API, STATE } from '../constants';
import { isArrayNotEmpty } from '../utils/js/array';
import { createScriptTag } from '../utils/js/window';
import { types } from '../utils/js/types';
import { default as googleMapsSelectors } from '../selectors/googleMaps';
import { setGmiState } from './map';

/**
 * @name onMapsApiLoaded
 * @desc Generates a closure to be used as a callback called after the maps API successfully loads
 * @param {function} dispatch - redux dispatch function
 * @param {function} callback - custom callback that's fired when the lib is succesfully loaded
 * @param {Array<string>} libraries - libraries loaded along with the API.
 * @return {function}
 */
const onMapsApiLoaded = ({ dispatch, callback, libraries }) => () => {
  if (window.google && window.google.maps) {
    dispatch(setGmiState(STATE.UI.GOOGLE_MAPS.API_LOADED, true));
    if (isArrayNotEmpty(libraries)) {
      dispatch(setGmiState(STATE.UI.GOOGLE_MAPS.LIBRARIES, libraries));
    }
    if (types(callback).isFunction) {
      callback();
    }
  }
};

/**
 * @name loadGoogleMapsApi
 * @desc Loads the google maps API by appending a script tag to the window object
 * @param {object} config - an object of configuration properties for initializing the Google Maps API
 * @param {string} config.key - key required to make use of Google Maps API.
 *  More info on API keys: https://developers.google.com/maps/documentation/javascript/get-api-key
 * @param {string} config.language - code for the language in which the API should be loaded.
 *  If omitted, the API will default to user-agent language by itself, but we opt-in to `en` by ourselves.
 * @param {Array<string>} config.libraries - (optional) Libraries to be loaded along with the API.
 *  NOTE: all libraries must be enabled on the Google Maps API console. Follow the API key docs for instructions.
 *  More info on available libraries: https://developers.google.com/maps/documentation/javascript/libraries
 * @param {function} config.callback - (optional) callback that's fired when the lib is succesfully loaded
 *
 * @return {function} a standard thunk function allowing for additional sync/async logic to be called
 */
export const loadGoogleMapsApi = ({
  key,
  language = 'en',
  libraries,
  callback
}) => (dispatch, getState) => {
  // if the script is already loaded for some reason, just exit
  const isGoogleMapsApiLoaded = googleMapsSelectors.apiLoaded(getState());
  if (isGoogleMapsApiLoaded) return;

  window.onMapsApiLoaded = onMapsApiLoaded({ dispatch, callback, libraries });

  const src = `${EXTERNAL_API.GOOGLE_MAPS}?key=${key}` +
    `&language=${language}` +
    (isArrayNotEmpty(libraries) ? `&libraries=${libraries.join(',')}` : '') +
    '&callback=onMapsApiLoaded';

  createScriptTag(src);
};
